import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import { Avatar, Badge } from '@repletoryreach/sdk-ui';
import type { AuditRow } from '../../../data/useAuditFeed';
import type { Indexed } from '../../../lib/types';
import { TenantName } from '../../../components/TenantName';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { useTenantMap } from '../../../data/useTenants';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import { SOURCE_COLOR, SOURCE_LABEL_KEY } from './sourceColors';

export function AuditTable({ rows, loading, onOpen }: {
  rows: AuditRow[];
  loading: boolean;
  onOpen: (row: AuditRow) => void;
}) {
  const t = useT();
  const { map: tenants } = useTenantMap();
  return (
    <DataTable<Indexed<AuditRow>>
      records={rows as Indexed<AuditRow>[]}
      loading={loading}
      // Source plus id is unique across the fan-in, so the key survives a
      // re-sort. Folding the array index in used to remount every row.
      idAccessor={(r) => `${r.source}-${r.id}`}
      emptyState={t('audit.emptyRows')}
      highlightOnHover
      onRowClick={(r) => onOpen(r)}
      columns={[
        {
          accessor: 'occurred_at',
          title: t('audit.columns.when'),
          sortable: true,
          // The exact moment sits under the relative one, because "3h ago" is
          // what a person scans by and the timestamp is what they quote.
          render: (r) => (
            <time dateTime={r.occurred_at} title={formatDateTime(r.occurred_at)}>
              <TwoLineCell primary={formatRelative(r.occurred_at)} secondary={formatDateTime(r.occurred_at)} />
            </time>
          )
        },
        {
          accessor: 'source',
          title: t('audit.columns.source'),
          render: (r) => (
            <Badge size="xs" color={SOURCE_COLOR[r.source]} variant="light">
              {t(SOURCE_LABEL_KEY[r.source])}
            </Badge>
          )
        },
        {
          accessor: 'organisation_id',
          title: t('audit.columns.org'),
          render: (r) => {
            const tenant = tenants.get(r.organisation_id);
            return (
              <TwoLineCell
                leading={<Avatar name={tenant?.name ?? '?'} size={26} />}
                primary={<TenantName id={r.organisation_id} />}
                secondary={tenant?.slug}
              />
            );
          }
        },
        {
          accessor: 'summary',
          title: t('audit.columns.summary'),
          render: (r) => <TwoLineCell primary={r.summary} secondary={r.detail} />
        }
      ]}
    />
  );
}
